// Запись журнала действий (data-model.md, audit.entries; contracts/api.md, AuditEntry).
// Откуда действие — поле source; канал сообщений и ответов добавит 011 отдельным полем channel.
import { z } from '../kernel/zod.ts';
import { IdSchema, TimestampSchema } from '../kernel/common.ts';

/** Кто действовал: пользователь, сам сервис или команда установки на сервере. */
export const actorKinds = ['user', 'system', 'installer'] as const;
export const ActorKindSchema = z.enum(actorKinds);
export type ActorKind = (typeof actorKinds)[number];

/**
 * Откуда действие: интерфейс, команда на сервере, сам сервис (FR-031), агент по MCP-доступу —
 * от имени пользователя ключа (005, FR-032; значение в базе добавляет миграция audit дорожки S2).
 */
export const auditSources = ['web', 'cli', 'system', 'agent'] as const;
export const AuditSourceSchema = z.enum(auditSources);
export type AuditSource = (typeof auditSources)[number];

/** Итог: выполнено или отказ в доступе (FR-016а). */
export const auditResults = ['ok', 'denied'] as const;
export const AuditResultSchema = z.enum(auditResults);
export type AuditResult = (typeof auditResults)[number];

/** access — пользователи и доступ, видит только администратор клиента (FR-034а); content — остальное. */
export const auditSensitivities = ['access', 'content'] as const;
export const AuditSensitivitySchema = z.enum(auditSensitivities);
export type AuditSensitivity = (typeof auditSensitivities)[number];

/**
 * Коды действий в поле operation (data-model.md, «Матрица операций», колонка «Журнал»,
 * contracts/events.md 001 и 002, contracts/api.md 003 и 005). Запись об отказе несёт код операции
 * матрицы прав, например users.read.
 */
export const auditActions = [
  'paste.analysis_started',
  'paste.analysis_cancelled',
  'paste.decisions_confirmed',

  'auth.login',
  'auth.login_failed',
  'auth.logout',
  'user.created',
  'user.updated',
  'user.archived',
  'user.restored',
  'user.locked',
  'user.unlocked',
  // «Отменить» после «Снять блокировку» (T134): блокировка до прежнего срока.
  'user.unlock_undone',
  'invite.issued',
  'invite.used',
  'password.reset',
  'password.changed',
  'password.change_failed',
  // 025: пользователь сам запросил ссылку восстановления пароля (только существующая учётная запись).
  'password.recovery_requested',
  // 035: «Отправить письмо заново» — новое приветственное письмо.
  'user.welcome_requested',
  'sessions.revoked',
  'role.assigned',
  'role.changed',
  'role.removed',
  'role.restored',
  'substitution.created',
  'substitution.cancelled',
  'substitution.ended',
  'support.granted',
  'support.revoked',
  'support.expired',
  'audit.exported',
  // 002: фоновые задачи и выгрузка журнала в фоне.
  'job.retried',
  'job.retry_undone',
  'job.cancelled',
  'audit.export.requested',
  'audit.export.downloaded',
  'audit.export.deleted',
  // 003: загрузка файла протокола (contracts/api.md 003, «Журнал действий»).
  'document.created',
  'document.manager_changed',
  'document.version_uploaded',
  // 005: справочник, настройки, ключи агента (contracts/api.md 005, «Журнал действий»).
  'directory.record_created',
  'directory.record_updated',
  'directory.record_archived',
  'directory.record_restored',
  'directory.email_set',
  'directory.email_confirmed',
  'directory.mail_flag_set',
  'directory.workplace_added',
  'directory.workplace_updated',
  'directory.workplace_removed',
  'directory.curator_added',
  'directory.curator_updated',
  'directory.curator_removed',
  'directory.zone_set',
  'directory.alias_added',
  'directory.alias_rejected',
  'directory.alias_reject_undone',
  'directory.mention_resolved',
  'directory.pair_suggested',
  'directory.pair_decided',
  'directory.merged',
  'directory.unmerged',
  // 005-2: смена вида записи (contracts/api.md 005-2, «Журнал действий»).
  'directory.kind_changed',
  'directory.kind_change_undone',
  'directory.kind_reviewed',
  'directory.kind_review_undone',
  // 006: подразделения и массовые действия.
  'directory.unit_created',
  'directory.unit_updated',
  'directory.unit_email_set',
  'directory.unit_archived',
  'directory.unit_restored',
  'directory.bulk_applied',
  'directory.bulk_undone',
  'settings.changed',
  'agent_key.issued',
  'agent_key.revoked',
  // 008: канал почты — подключение, ключ, пробное письмо (contracts/api.md 008, «Журнал действий»).
  'delivery.connection_saved',
  'delivery.key_set',
  'delivery.key_removed',
  'delivery.sending_switched',
  'delivery.reports_registered',
  'delivery.tracking_disabled',
  'delivery.test_letter_sent',
  // Сходимость 008 (T041, принцип VII): «Проверить», «Настроить отчёты», ответное письмо.
  'delivery.check_requested',
  'delivery.reports_requested',
  'delivery.reply_received',
  // 010: схемы шаблонов (contracts/api.md 010, «Журнал действий»).
  'template.connected',
  'template.version_created',
  'template.renamed',
  'template.archived',
  'template.restored',
  // 011: протокол в системе (contracts/api.md 011, «Журнал действий»).
  'protocol.transferred',
  'protocol.reverted',
  'protocol.cells_edited',
  'protocol.row_inserted',
  'protocol.parent_resolved',
  // 012: команды дерева, разделение, отмена и повтор (contracts/api.md 012, «Журнал действий»).
  'protocol.branches_moved',
  'protocol.branches_copied',
  'protocol.branches_archived',
  'protocol.branches_restored',
  'protocol.item_split',
  'protocol.undone',
  'protocol.redone',
  // 013: пакетная правка, оформление, скрытие в протоколе, дополнительные поля
  // (contracts/api.md 013, «Журнал действий»).
  'protocol.cells_batch',
  'protocol.cells_styled',
  'protocol.rows_hidden',
  'protocol.rows_shown',
  'protocol.field_added',
  'protocol.field_renamed',
  // 014: ответственные, состояние работы, протокол без файла (contracts/api.md 014, «Журнал»).
  'protocol.assignees_set',
  'protocol.work_state_set',
  'protocol.created',
  // 016: фиксация недели и выгрузка протокола (contracts/api.md 016, «Журнал действий»).
  'protocol.week_snapshot',
  'protocol.exported',
  // 017: границы и объединения ячеек, перенос из файла (contracts/api.md 017, «Журнал действий»).
  'protocol.cells_bordered',
  'protocol.cells_merged',
  'protocol.borders_backfilled',
  // 020: доступ к протоколу (contracts/api.md 020, «Журнал действий»).
  'protocol.access_granted',
  'protocol.access_changed',
  'protocol.access_removed',
  'protocol.sharing_changed',
  'protocol.access_invited',
  // 027: контрагенты пункта, перенос и замена записей, перенос 027 (contracts/api.md 027).
  'protocol.contractors_set',
  'protocol.parties_replaced',
  'protocol.parties_backfilled',
  // 0.20.1: перенос строк вкладки между полями и «Разделить…».
  'protocol.parties_moved',
  'protocol.parties_split',
  // 029: история версий (contracts/api.md 029, «Журнал действий»): без значений ячеек, имён людей
  // и текста имени — ревизии и числа.
  'protocol.version_restored',
  'protocol.version_reverted',
  'protocol.version_named',
  // 030: связь учётной записи с человеком, ссылка входа, «Первое письмо», оспаривание назначения
  // (contracts/api.md 030, «Журнал действий»): без токенов, кодов и адресов.
  'account.person_linked',
  'login_link.issued',
  'login_link.used',
  'login_link.revoked',
  'login_link.not_me',
  'first_letter.scheduled',
  'first_letter.cancelled',
  'item.link_disputed',
  'item.reported',
  'item.link_check_decided',
  // 033: стоп-кран и рассылка протокола (contracts/api.md 033, «Журнал действий»): без адресов.
  'delivery.messages_stopped',
  'delivery.messages_resumed',
  'delivery.mailing_changed',
  // 031: день планёрки протокола (contracts/api.md 031, §4).
  'delivery.meeting_day_changed',
  // 032: ответ из письма, отписка, отъезд, копия руководителю (contracts/api.md 032, §4):
  // без ФИО, адресов, токенов и ссылок.
  'answer_link.issued',
  'answer_link.revoked',
  'answer.accepted',
  'answer.repeat',
  'answer.entered',
  'unsubscribe.created',
  'unsubscribe.removed',
  'absence.set',
  'absence.cleared',
  'manager_copy.sent',
] as const;
export type AuditAction = (typeof auditActions)[number];

/** «Было» и «стало»: поля записи. Пароли и токены сюда не пишутся никогда (FR-031б). */
export const AuditValuesSchema = z.record(z.string(), z.unknown());

export const AuditEntrySchema = z.object({
  id: IdSchema,
  at: TimestampSchema,
  actor: z.object({
    kind: ActorKindSchema,
    userId: IdSchema.optional(),
    /** ФИО из текущей записи пользователя, а не из журнала (FR-033). */
    name: z.string().optional(),
  }),
  source: AuditSourceSchema,
  /** Ключ агента при source = agent: номер и имя; сам ключ в журнал не пишется (005, FR-032). */
  agentKey: z.object({ id: IdSchema, name: z.string() }).optional(),
  /** Код действия (auditActions) или, для отказа, код операции матрицы прав. */
  operation: z.string(),
  entity: z.object({
    type: z.string(),
    /** Нет у отказа в списке, где записи ещё нет. */
    id: IdSchema.optional(),
    title: z.string().optional(),
  }),
  before: AuditValuesSchema.optional(),
  after: AuditValuesSchema.optional(),
  /**
   * ФИО людей из «было — стало» по номеру пользователя (userId, ownerUserId, deputyUserId,
   * supportUserId…) — из текущей записи пользователя, а не из журнала (FR-033, T072). Сами
   * «было — стало» хранят номера: показ подставляет ФИО отсюда.
   */
  names: z.record(z.string(), z.string()).optional(),
  result: AuditResultSchema,
});

export type AuditEntry = z.infer<typeof AuditEntrySchema>;

/** Сколько изменений после If-Match отдаёт 409 REC-001: число всех — текущая версия − If-Match. */
export const CONFLICT_CHANGES_LIMIT = 50;

/**
 * 409 REC-001 (FR-028, SC-004; T124): изменения, поднявшие версию записи после версии из
 * If-Match, новые сверху, — кто, когда, «было — стало». Вход, выпуск ссылки, завершение сеансов
 * и отказы версию не меняют и сюда не попадают. Первое из них — lastChange.
 */
export const ConflictChangesSchema = z.array(AuditEntrySchema).max(CONFLICT_CHANGES_LIMIT);
